<template>
  <b-modal
    id="supplement-info-modal"
    ref="supplementModal"
    centered
    size="lg"
    title="Bổ sung thông tin phiếu thu"
    hide-footer
    @hide="resetForm"
  >
    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(onSubmit)">
        <!-- Mã giao dịch -->
        <div class="form-group">
          <ValidationProvider rules="required" vid="transactionCode" name="Mã giao dịch">
            <div slot-scope="{ errors }">
              <label for="transactionCode" class="form-label">
                Mã giao dịch <span class="text-danger">*</span>
              </label>
              <input
                id="transactionCode"
                v-model="form.transactionCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors[0] }"
                placeholder="Nhập mã giao dịch"
              />
              <div class="invalid-feedback">{{ errors[0] }}</div>
            </div>
          </ValidationProvider>
        </div>

        <!-- File phụ lục -->
        <div class="form-group">
          <label class="form-label">File phụ lục (nhiều)</label>
          <div class="upload-area" @click="$refs.appendixFileInput.click()">
            <div v-if="form.appendixFiles.length === 0" class="upload-placeholder">
              <i class="bx bx-cloud-upload"></i>
              <p>Click để chọn file phụ lục</p>
              <small class="text-muted">Hỗ trợ: PDF, DOC, DOCX, XLS, XLSX</small>
            </div>
            <div v-else class="uploaded-files">
              <div v-for="(file, index) in form.appendixFiles" :key="index" class="file-item">
                <i class="bx bxs-file-pdf"></i>
                <div class="file-info">
                  <span class="file-name">{{ getFileName(file) }}</span>
                  <small class="file-status" :class="getStatusClass(file.status)">
                    {{ getFileStatus(file) }}
                  </small>
                </div>
                <button type="button" class="btn-remove" @click.stop="removeAppendixFile(index)">
                  <i class="bx bx-x"></i>
                </button>
              </div>
              <!-- Icon plus để thêm file -->
              <div class="add-more-item" @click.stop="$refs.appendixFileInput.click()">
                <i class="bx bx-plus"></i>
                <span>Thêm file</span>
              </div>
            </div>
          </div>
          <input
            ref="appendixFileInput"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx"
            style="display: none"
            @change="handleAppendixFiles"
          />
        </div>

        <!-- Ảnh chuyển khoản -->
        <div class="form-group">
          <label class="form-label">Ảnh chuyển khoản (nhiều)</label>
          <div class="upload-area" @click="$refs.imageInput.click()">
            <div v-if="form.uploadImgs.length === 0" class="upload-placeholder">
              <i class="bx bx-image"></i>
              <p>Click để chọn ảnh chuyển khoản</p>
              <small class="text-muted">Hỗ trợ: JPG, PNG, JPEG</small>
            </div>
            <div v-else class="uploaded-images">
              <div v-for="(image, index) in form.uploadImgs" :key="index" class="image-item">
                <img :src="getImagePreview(image)" alt="Preview" />
                <div class="image-status" :class="getStatusClass(image.status)">
                  {{ getFileStatus(image) }}
                </div>
                <button type="button" class="btn-remove" @click.stop="removeImage(index)">
                  <i class="bx bx-x"></i>
                </button>
              </div>
              <!-- Icon plus để thêm ảnh -->
              <div class="add-more-image" @click.stop="$refs.imageInput.click()">
                <i class="bx bx-plus"></i>
              </div>
            </div>
          </div>
          <input
            ref="imageInput"
            type="file"
            multiple
            accept="image/*"
            style="display: none"
            @change="handleImages"
          />
        </div>

        <!-- Buttons -->
        <div class="modal-footer border-0 px-0">
          <button type="button" class="btn btn-light" @click="$refs.supplementModal.hide()">
            Hủy
          </button>
          <button type="submit" class="btn btn-primary" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
            Lưu
          </button>
        </div>
      </form>
    </ValidationObserver>
  </b-modal>
</template>

<script>
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import { CommonService } from '@/services/common.service'
  import { DeviceService } from '@/services/device.service'

  export default {
    name: 'SupplementInfoModal',
    components: {
      ValidationObserver,
      ValidationProvider,
    },
    props: {
      receiptId: {
        type: String,
        required: true,
        default: '',
      },
    },
    data() {
      return {
        loading: false,
        form: {
          transactionCode: '',
          uploadImgs: [], // Sẽ chứa { file: File, url: null, status: 'pending' }
          appendixFiles: [], // Sẽ chứa { file: File, url: null, status: 'pending' }
        },
      }
    },
    methods: {
      show() {
        this.$refs.supplementModal.show()
      },

      hide() {
        this.$refs.supplementModal.hide()
      },

      resetForm() {
        this.form = {
          transactionCode: '',
          uploadImgs: [],
          appendixFiles: [],
        }
        this.$nextTick(() => {
          this.$refs.observer.reset()
        })
      },

      async handleAppendixFiles(event) {
        const files = Array.from(event.target.files)

        for (const file of files) {
          const fileItem = {
            file: file,
            url: null,
            status: 'pending',
            name: file.name,
          }
          this.form.appendixFiles.push(fileItem)
        }

        // Reset input
        event.target.value = ''
      },

      async handleImages(event) {
        const files = Array.from(event.target.files)

        for (const file of files) {
          const imageItem = {
            file: file,
            url: null,
            status: 'pending',
            name: file.name,
            preview: URL.createObjectURL(file), // Tạo preview cho ảnh
          }
          this.form.uploadImgs.push(imageItem)
        }

        // Reset input
        event.target.value = ''
      },

      async sendFile(item, type) {
        if (item.file && item.status !== 'uploaded') {
          const formData = new FormData()
          formData.append('files', item.file)
          formData.append('folderName', 'AgentReceipt') // Tên folder cho phiếu thu đại lý

          try {
            let data
            if (type === 'file') {
              // Gọi API upload file (PDF, DOC, XLS...)
              data = (await this.$parent.uploadFile)
                ? await this.$parent.uploadFile(formData)
                : await CommonService.uploadFile(formData)
            } else if (type === 'image') {
              // Gọi API upload image
              data = (await this.$parent.uploadImg)
                ? await this.$parent.uploadImg(formData)
                : await CommonService.uploadImg(formData)
            }

            const urlFile = data.data[0]
            item.url = urlFile
            item.status = 'uploaded'
            return urlFile
          } catch (error) {
            item.status = 'error'
            throw new Error('Lỗi upload file: ' + error.message)
          }
        } else {
          return item.url ?? item
        }
      },

      async uploadAllFiles() {
        // Upload tất cả file phụ lục
        const appendixUrls = await Promise.all(
          this.form.appendixFiles.map(file => this.sendFile(file, 'file'))
        )

        // Upload tất cả ảnh
        const imageUrls = await Promise.all(
          this.form.uploadImgs.map(image => this.sendFile(image, 'image'))
        )

        return {
          appendixFiles: appendixUrls,
          uploadImgs: imageUrls,
        }
      },

      removeAppendixFile(index) {
        this.form.appendixFiles.splice(index, 1)
      },

      removeImage(index) {
        this.form.uploadImgs.splice(index, 1)
      },

      getFileName(fileItem) {
        return (
          fileItem.name ||
          fileItem.file?.name ||
          `File ${this.form.appendixFiles.indexOf(fileItem) + 1}`
        )
      },

      getImagePreview(imageItem) {
        return imageItem.preview || imageItem.url || imageItem
      },

      getFileStatus(fileItem) {
        if (fileItem.status === 'pending') return 'Chờ upload'
        if (fileItem.status === 'uploaded') return 'Đã upload'
        if (fileItem.status === 'error') return 'Lỗi upload'
        return ''
      },

      getStatusClass(status) {
        if (status === 'pending') return 'status-pending'
        if (status === 'uploaded') return 'status-success'
        if (status === 'error') return 'status-error'
        return ''
      },

      async onSubmit() {
        this.loading = true

        try {
          // Kiểm tra có file/ảnh nào cần upload không
          if (this.form.appendixFiles.length === 0 && this.form.uploadImgs.length === 0) {
            this.$emit('show-error', 'Vui lòng chọn ít nhất 1 file hoặc ảnh!')
            return
          }

          // Upload tất cả file lên S3
          const uploadResults = await this.uploadAllFiles()

          const request = {
            receiptId: this.receiptId,
            transactionCode: this.form.transactionCode,
            uploadImgs: uploadResults.uploadImgs,
            appendixFiles: uploadResults.appendixFiles,
          }
          const res = await DeviceService.updateAgencyInfo(request)
          if (res.code === 1000) {
            this.hide()
            this.$emit('supplement-success')

            // Hiển thị thông báo thành công
            this.$emit('show-success', 'Bổ sung thông tin thành công!')
          } else {
            this.$emit('show-error', res.message || 'Có lỗi xảy ra khi bổ sung thông tin!')
          }
        } catch (error) {
          console.error('Error:', error)
          this.$emit('show-error', error.message || 'Có lỗi xảy ra khi bổ sung thông tin!')
        } finally {
          this.loading = false
        }
      },
    },
  }
</script>

<style scoped lang="scss">
  .upload-area {
    border: 2px dashed #ddd;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border-color: #007bff;
      background-color: #f8f9fa;
    }
  }

  .upload-placeholder {
    i {
      font-size: 48px;
      color: #6c757d;
      margin-bottom: 10px;
    }

    p {
      margin-bottom: 5px;
      color: #495057;
    }

    small {
      color: #6c757d;
    }
  }

  .uploaded-files {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .file-item {
    display: flex;
    align-items: center;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;

    i {
      font-size: 20px;
      color: #dc3545;
      margin-right: 10px;
    }

    span {
      flex-grow: 1;
      font-size: 14px;
    }
  }

  .add-more-item {
    display: flex;
    align-items: center;
    padding: 10px;
    background-color: #fff;
    border: 2px dashed #007bff;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #007bff;

    &:hover {
      background-color: #f8f9fa;
      border-color: #0056b3;
    }

    i {
      font-size: 18px;
      margin-right: 8px;
    }

    span {
      font-size: 14px;
      font-weight: 500;
    }
  }

  .uploaded-images {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;
    width: 100%;
  }

  .image-item {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #e9ecef;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .add-more-image {
    width: 100px;
    height: 100px;
    border: 2px dashed #007bff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: #fff;

    &:hover {
      background-color: #f8f9fa;
      border-color: #0056b3;
    }

    i {
      font-size: 24px;
      color: #007bff;
    }
  }

  .btn-remove {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #dc3545;
    color: white;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 12px;

    &:hover {
      background-color: #c82333;
    }
  }

  .file-item .btn-remove {
    position: static;
    margin-left: 10px;
  }

  .form-label {
    font-weight: 600;
    margin-bottom: 8px;
  }

  .text-danger {
    color: #dc3545 !important;
  }

  .is-invalid {
    border-color: #dc3545;
  }

  .invalid-feedback {
    display: block;
    color: #dc3545;
    font-size: 0.875em;
    margin-top: 0.25rem;
  }
</style>
